import * as THREE from 'three';

// ============================================================================
// Meadow - a clean, self-contained world for showing imported SketchUp
// exports: a wide flat grass meadow at the spawn, gentle rolling fields that
// build into hazy far ridge silhouettes against the sky. It is built entirely
// from procedural geometry (no DEM / satellite fetch), so it loads instantly
// and offline. It speaks the same small terrain interface the store consumes
// ({ group, heightAt, lonLatToWorld }), so Character and ImportedScene work
// unchanged - the character spawns at the origin and imported models are
// placed on flat meadow ground right next to him.
// ============================================================================

const R_MEADOW = 1150;          // core radius that stays (almost) flat
const R_FAR = 3700;             // ridge line starts ramping beyond this
const EDGE = 4300;              // half-size of the square ground patch
const GRASS_BASE = [0.41, 0.50, 0.30];
const GRASS_MID = [0.52, 0.58, 0.40];
const HILL_TINT = [0.58, 0.63, 0.55];

const rgb = (v) => new THREE.Color(v[0], v[1], v[2]);

function smooth(t) {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
}

function heightField(x, z) {
  const r = Math.hypot(x, z);
  if (r <= R_MEADOW) return 0;
  const t = smooth((r - R_MEADOW) / (R_FAR - R_MEADOW));
  const a = Math.atan2(z, x);
  // angular ridge modulation so the far ring reads as uneven mountain humps
  const ridges =
    1 +
    0.28 * Math.sin(a * 2 + 1.7) +
    0.22 * Math.sin(a * 5 + 4.1) +
    0.18 * Math.sin(a * 7 + 0.9);
  const lift = 12 + 118 * t * t;
  return lift * ridges * t;
}

export class Meadow {
  constructor(quality) {
    const lowDetail = quality === 'low';
    const seg = lowDetail ? 120 : 220;
    this.disposed = false;

    const group = new THREE.Group();
    group.name = 'meadow';

    // ---- single square ground patch, vertex-coloured and height-mapped ----
    const size = EDGE * 2;
    const geo = new THREE.PlaneGeometry(size, size, seg, seg);
    geo.rotateX(-Math.PI / 2);
    const pos = geo.attributes.position;
    const colors = new Float32Array(pos.count * 3);
    const baseColor = rgb(GRASS_BASE);
    const midColor = rgb(GRASS_MID);
    const hillColor = rgb(HILL_TINT);

    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const r = Math.hypot(x, z);
      const y = heightField(x, z);
      pos.setY(i, y);
      const t = smooth((r - R_MEADOW) / (R_FAR - R_MEADOW));

      let col;
      if (r < R_MEADOW * 0.55) {
        // mowed meadow nearest spawn: keep it even and bright
        col = baseColor.clone().offsetHSL(0, 0, 0.06);
      } else if (r < R_FAR) {
        col = midColor.clone();
      } else {
        col = hillColor.clone();
      }
      // subtle angular tint variation so the plane doesn't look flat-painted
      const a = Math.atan2(z, x);
      const grain = 1 + 0.045 * Math.sin(a * 3 + x * 0.0004 + z * 0.0003);
      colors[i * 3] = col.r * grain;
      colors[i * 3 + 1] = col.g * grain;
      colors[i * 3 + 2] = col.b * grain;
      void t;
    }

    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geo.computeVertexNormals();
    const mat = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 1,
      metalness: 0,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    group.add(mesh);

    // A few soft distant hill "crown" blobs sit on the ridge ring so the
    // silhouette has a little more character than a smooth slope.
    if (!lowDetail) {
      const hillGeo = new THREE.IcosahedronGeometry(1, 1);
      const hillMat = new THREE.MeshStandardMaterial({
        color: 0x7d8b8f,
        roughness: 1,
        metalness: 0,
        flatShading: true,
      });
      const spots = [
        [0.0, 1.0], [0.45, 0.9], [0.9, 0.55], [1.0, 0.15], [0.65, -0.8],
        [0.2, -1.0], [-0.35, -0.85], [-0.8, -0.5], [-0.95, 0.1], [-0.7, 0.8],
      ];
      const RI = 3550;
      for (const [nx, nz] of spots) {
        const a = Math.atan2(nz, nx);
        const rr = RI + 120 * Math.sin(a * 4 + 2);
        const scale = 210 + 160 * Math.sin(a * 5 + 1.3);
        const blob = new THREE.Mesh(hillGeo, hillMat);
        blob.position.set(Math.cos(a) * rr, scale * 0.42, Math.sin(a) * rr);
        blob.scale.set(scale * 1.6, scale * 0.8, scale * 1.2);
        blob.rotation.set(0, a, 0);
        blob.matrixAutoUpdate = false;
        blob.updateMatrix();
        group.add(blob);
      }
    }

    this.group = group;

    // ---- terrain-ish interface used by Character / ImportedScene ---------
    this.heightAt = (x, z) => heightField(x, z);
    this.lonLatToWorld = () => ({ x: 0, y: 0, z: 0 });
  }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.group.traverse((obj) => {
      if (obj.isMesh) {
        obj.geometry?.dispose();
        if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
        else obj.material?.dispose();
      }
    });
  }
}

// Factory kept named like Terrain.load for symmetry.
export async function loadMeadow(quality = 'high') {
  return new Meadow(quality);
}
